import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Mail, Shield, Camera, Loader2, Eye, EyeOff, Pencil, KeyRound } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const roleLabels = {
  super_admin: "Super Admin",
  marketing: "Marketing",
  cco: "CCO",
};

const roleBadgeColors = {
  super_admin: "bg-purple-100 text-purple-700 border-purple-200",
  marketing: "bg-blue-100 text-blue-700 border-blue-200",
  cco: "bg-orange-100 text-orange-700 border-orange-200",
};

const TeamPage = () => {
  const { user: currentUser } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [editData, setEditData] = useState({ name: "", email: "", role: "marketing", assignable: true });
  const [resetPwValue, setResetPwValue] = useState("");
  const [showResetPw, setShowResetPw] = useState(false);
  const [resettingPw, setResettingPw] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "marketing",
    assignable: true,
  });

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/team`);
      setTeamMembers(response.data);
    } catch (error) {
      toast.error("Failed to fetch team members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTeamMembers(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await axios.post(`${API}/team`, formData);
      toast.success("Team member added successfully");
      setDialogOpen(false);
      setFormData({ name: "", email: "", password: "", role: "marketing", assignable: true });
      fetchTeamMembers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to add team member");
    }
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm("Are you sure you want to remove this team member?")) return;
    try {
      await axios.delete(`${API}/team/${memberId}`);
      toast.success("Team member removed successfully");
      fetchTeamMembers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to remove team member");
    }
  };

  const handlePicUpload = async (e, memberId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Only JPEG, PNG or WebP images allowed");
      return;
    }
    setUploadingFor(memberId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/users/${memberId}/upload-profile-pic`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Profile picture updated");
      fetchTeamMembers();
    } catch {
      toast.error("Failed to upload picture");
    } finally {
      setUploadingFor(null);
    }
  };

  const getInitials = (name) => name?.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "?";

  const getProfilePicUrl = (member) => member.profile_pic ? `${BASE_URL}${member.profile_pic}` : null;

  const openEditDialog = (member) => {
    setEditMember(member);
    setEditData({ name: member.name, email: member.email, role: member.role, assignable: member.assignable !== false });
    setResetPwValue("");
    setShowResetPw(false);
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editMember) return;
    setSavingEdit(true);
    try {
      await axios.put(`${API}/team/${editMember.id}`, editData);
      toast.success("Member updated successfully");
      setEditDialogOpen(false);
      fetchTeamMembers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to update member");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleResetPassword = async () => {
    if (!editMember || resetPwValue.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setResettingPw(true);
    try {
      const { data } = await axios.post(`${API}/team/${editMember.id}/reset-password`, { new_password: resetPwValue });
      toast.success(data.message || "Password reset successfully");
      setResetPwValue("");
      setShowResetPw(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to reset password");
    } finally {
      setResettingPw(false);
    }
  };

  const isSuperAdmin = currentUser?.role === "super_admin";
  const canManageTeam = currentUser?.role === "super_admin";

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto animate-fade-in" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Team</h1>
          <p className="text-sm text-gray-500 mt-1">{canManageTeam ? "Manage team members and access levels" : "View team members"}</p>
        </div>
        {canManageTeam && (
          <Button
            size="sm"
            onClick={() => setDialogOpen(true)}
            className="bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white rounded-lg"
            data-testid="add-team-member-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Member
          </Button>
        )}
      </div>

      {/* Team Members Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[1, 2, 3].map(i => (
            <Card key={i} className="border border-gray-200 rounded-xl">
              <CardContent className="p-5"><div className="h-20 bg-gray-100 rounded-lg animate-pulse" /></CardContent>
            </Card>
          ))}
        </div>
      ) : teamMembers.length === 0 ? (
        <Card className="border border-gray-200 rounded-xl bg-white">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-xl flex items-center justify-center">
              <Shield className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No team members yet</h3>
            <p className="text-sm text-gray-500 mb-6">Add team members to assign leads</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white rounded-lg">
              <Plus className="w-4 h-4 mr-2" /> Add First Member
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {teamMembers.map(member => (
            <Card key={member.id} className="border border-gray-200 rounded-xl bg-white hover:border-gray-300 transition-colors" data-testid={`team-member-${member.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="relative group">
                      <Avatar className="w-12 h-12 border-2 border-gray-100">
                        <AvatarImage src={getProfilePicUrl(member)} alt={member.name} />
                        <AvatarFallback className="bg-[#0D0D5B] text-white text-sm font-medium">
                          {getInitials(member.name)}
                        </AvatarFallback>
                      </Avatar>
                      <button
                        onClick={() => {
                          fileInputRef.current?.setAttribute("data-member-id", member.id);
                          fileInputRef.current?.click();
                        }}
                        disabled={uploadingFor === member.id}
                        className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        data-testid={`upload-pic-${member.id}`}
                      >
                        {uploadingFor === member.id ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Camera className="w-4 h-4 text-white" />}
                      </button>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{member.name}</h3>
                      <div className="flex items-center gap-1 text-sm text-gray-500 mt-0.5">
                        <Mail className="w-3 h-3" />
                        <span>{member.email}</span>
                      </div>
                      <Badge className={`mt-1 text-xs ${roleBadgeColors[member.role] || "bg-gray-100 text-gray-700"}`}>
                        {roleLabels[member.role] || member.role}
                      </Badge>
                      {member.assignable !== false ? (
                        <span className="mt-0.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">Assignable</span>
                      ) : (
                        <span className="mt-0.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500 border border-gray-200">Not Assignable</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {canManageTeam && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(member)}
                        className="text-gray-400 hover:text-[#0D0D5B] hover:bg-[#0D0D5B]/5 h-8 w-8 p-0 rounded-lg"
                        data-testid={`edit-member-${member.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    {canManageTeam && currentUser?.id !== member.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(member.id)}
                        className="text-gray-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0 rounded-lg"
                        data-testid={`delete-member-${member.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Hidden file input for profile pic uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(e) => {
          const memberId = fileInputRef.current?.getAttribute("data-member-id");
          if (memberId) handlePicUpload(e, memberId);
        }}
        className="hidden"
      />

      {/* Add Member Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900">Add Team Member</DialogTitle>
            <DialogDescription className="text-sm text-gray-500">Create a new user account</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="add-member-form">
            <div className="space-y-2">
              <Label htmlFor="member-name">Name *</Label>
              <Input id="member-name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="border-gray-300 rounded-lg" data-testid="member-name-input" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="member-email">Email *</Label>
              <Input id="member-email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="border-gray-300 rounded-lg" data-testid="member-email-input" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="member-role">Access Level *</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                <SelectTrigger className="border-gray-300 rounded-lg" data-testid="member-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="cco">CCO</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label className="text-sm">Can be assigned to leads</Label>
                <p className="text-xs text-gray-400 mt-0.5">Appears in lead assignment dropdowns</p>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, assignable: !formData.assignable })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.assignable ? "bg-[#0D0D5B]" : "bg-gray-300"}`}
                data-testid="assignable-toggle"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${formData.assignable ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="member-password">Initial Password *</Label>
              <div className="relative">
                <Input id="member-password" type={showPassword ? "text" : "password"} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required minLength={6} placeholder="Min 6 characters" className="border-gray-300 rounded-lg pr-10" data-testid="member-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500">Member can change this later from their profile</p>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-gray-300 rounded-lg" data-testid="cancel-member-btn">Cancel</Button>
              <Button type="submit" className="bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white rounded-lg" data-testid="save-member-btn">Add Member</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog (Super Admin) */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900">Edit Team Member</DialogTitle>
            <DialogDescription className="text-sm text-gray-500">Update member details or reset their password</DialogDescription>
          </DialogHeader>

          <div className="space-y-4" data-testid="edit-member-form">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} className="border-gray-300 rounded-lg" data-testid="edit-name-input" />
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })} className="border-gray-300 rounded-lg" data-testid="edit-email-input" />
            </div>

            <div className="space-y-2">
              <Label>Access Level</Label>
              <Select value={editData.role} onValueChange={(v) => setEditData({ ...editData, role: v })}>
                <SelectTrigger className="border-gray-300 rounded-lg" data-testid="edit-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="cco">CCO</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label className="text-sm">Can be assigned to leads</Label>
                <p className="text-xs text-gray-400 mt-0.5">Appears in lead assignment dropdowns</p>
              </div>
              <button
                type="button"
                onClick={() => setEditData({ ...editData, assignable: !editData.assignable })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${editData.assignable ? "bg-[#0D0D5B]" : "bg-gray-300"}`}
                data-testid="edit-assignable-toggle"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${editData.assignable ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1 border-gray-300 rounded-lg">Cancel</Button>
              <Button onClick={handleEditSave} disabled={savingEdit} className="flex-1 bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white rounded-lg" data-testid="save-edit-btn">
                {savingEdit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save Changes
              </Button>
            </div>

            {/* Password Reset Section */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-sm font-medium flex items-center gap-1.5 mb-2">
                <KeyRound className="w-4 h-4" /> Reset Password
              </Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showResetPw ? "text" : "password"}
                    value={resetPwValue}
                    onChange={(e) => setResetPwValue(e.target.value)}
                    placeholder="New password (min 6 chars)"
                    className="border-gray-300 rounded-lg pr-10"
                    data-testid="reset-pw-input"
                  />
                  <button type="button" onClick={() => setShowResetPw(!showResetPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                    {showResetPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  onClick={handleResetPassword}
                  disabled={resettingPw || resetPwValue.length < 6}
                  variant="outline"
                  className="border-orange-200 text-orange-600 hover:bg-orange-50 rounded-lg shrink-0"
                  data-testid="reset-pw-btn"
                >
                  {resettingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset"}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamPage;
